import React, { useMemo, useState } from 'react';
import {
  Area,
  AreaChart,
  CartesianGrid,
  Cell,
  Pie,
  PieChart,
  ResponsiveContainer,
  Tooltip,
  XAxis,
  YAxis,
} from 'recharts';
import { ArrowDownRight, ArrowUpRight, Clock3, ShieldAlert, Wrench } from 'lucide-react';
import { useNavigate } from 'react-router-dom';
import { gatewayPathForTab } from '../governedMcpConstants';
import { logGovernedMcp } from '../governedMcpLogger';
import { useGovernedMcp } from '../governedMcpStore';
import {
  DEFAULT_FILTERS,
  buildDashboardModel,
  compactCount,
  type CapabilityView,
  type DashboardRange,
  type EndpointEnvFilter,
  type SourceKindFilter,
} from '../dashboardAnalytics';

const RANGES: { id: DashboardRange; label: string }[] = [
  { id: '24h', label: '24h' },
  { id: '7d', label: '7d' },
  { id: '30d', label: '30d' },
  { id: '90d', label: '90d' },
];

const ENV_OPTIONS: { id: EndpointEnvFilter; label: string }[] = [
  { id: 'all', label: 'All environments' },
  { id: 'production', label: 'Production' },
  { id: 'test', label: 'Test' },
  { id: 'development', label: 'Development' },
];

const SOURCE_OPTIONS: { id: SourceKindFilter; label: string }[] = [
  { id: 'all', label: 'All sources' },
  { id: 'data_model', label: 'Data model' },
  { id: 'workflow', label: 'Workflow' },
  { id: 'knowledge_base', label: 'Knowledge' },
  { id: 'external_mcp', label: 'External MCP' },
];

const CAP_VIEWS: { id: CapabilityView; label: string }[] = [
  { id: 'tool', label: 'Tools' },
  { id: 'resource', label: 'Resources' },
  { id: 'prompt', label: 'Prompts' },
];

const OUTCOME_COLORS: Record<string, { light: string; dark: string }> = {
  successful: { light: '#059669', dark: '#34d399' },
  blocked: { light: '#e11d48', dark: '#fb7185' },
  approval: { light: '#d97706', dark: '#fbbf24' },
  failed: { light: '#64748b', dark: '#94a3b8' },
  rate_limited: { light: '#4f46e5', dark: '#818cf8' },
};

const GOVERNANCE_TONES: Record<string, { light: string; dark: string }> = {
  pii: { light: '#0ea5e9', dark: '#38bdf8' },
  policy: { light: '#e11d48', dark: '#fb7185' },
  output: { light: '#059669', dark: '#34d399' },
  injection: { light: '#4f46e5', dark: '#818cf8' },
  secrets: { light: '#7c3aed', dark: '#a78bfa' },
  jailbreak: { light: '#d97706', dark: '#fbbf24' },
};

function cardClass(isLight: boolean): string {
  return `rounded-2xl p-4 ${
    isLight
      ? 'bg-white shadow-[0_1px_2px_rgba(16,24,40,0.04)] ring-1 ring-black/[0.04]'
      : 'bg-neutral-900 ring-1 ring-white/10'
  }`;
}

function ChartTooltip({
  active,
  payload,
  label,
  isLight,
}: {
  active?: boolean;
  payload?: { name: string; value: number; color?: string }[];
  label?: string;
  isLight: boolean;
}) {
  if (!active || !payload?.length) return null;
  return (
    <div
      className={`rounded-xl px-3 py-2 text-xs shadow-lg ${
        isLight ? 'bg-white ring-1 ring-black/10 text-neutral-700' : 'bg-neutral-800 ring-1 ring-white/10 text-neutral-200'
      }`}
    >
      {label ? <p className={`font-medium mb-1 ${isLight ? 'text-neutral-900' : 'text-white'}`}>{label}</p> : null}
      {payload.map((item) => (
        <p key={item.name} className="flex items-center gap-2">
          <span className="h-1.5 w-1.5 rounded-full" style={{ background: item.color }} />
          {item.name}: <span className="tabular-nums font-medium">{compactCount(item.value)}</span>
        </p>
      ))}
    </div>
  );
}

function pill(active: boolean, isLight: boolean): string {
  return `h-7 px-2.5 rounded-full text-xs font-medium transition-colors ${
    active
      ? isLight
        ? 'bg-neutral-900 text-white'
        : 'bg-white text-neutral-900'
      : isLight
        ? 'text-neutral-500 hover:text-neutral-800'
        : 'text-neutral-400 hover:text-white'
  }`;
}

function selectClass(isLight: boolean): string {
  return `h-8 rounded-lg border px-2 text-xs ${
    isLight ? 'bg-white border-neutral-200 text-neutral-700' : 'bg-neutral-950 border-neutral-700 text-neutral-200'
  }`;
}

function Delta({ value, invert, isLight }: { value: number; invert?: boolean; isLight: boolean }) {
  const up = value >= 0;
  const good = invert ? !up : up;
  const Icon = up ? ArrowUpRight : ArrowDownRight;
  return (
    <span
      className={`inline-flex items-center gap-0.5 text-[11px] font-medium tabular-nums ${
        good ? (isLight ? 'text-emerald-600' : 'text-emerald-400') : isLight ? 'text-rose-600' : 'text-rose-400'
      }`}
    >
      <Icon size={12} />
      {up ? '+' : ''}
      {value}%
    </span>
  );
}

function Spark({ values, color }: { values: number[]; color: string }) {
  const data = values.slice(-12).map((value, index) => ({ i: index, value }));
  return (
    <div className="h-9 w-24">
      <ResponsiveContainer width="100%" height="100%">
        <AreaChart data={data} margin={{ top: 2, right: 0, left: 0, bottom: 0 }}>
          <Area type="monotone" dataKey="value" stroke={color} fill={color} fillOpacity={0.16} strokeWidth={1.5} />
        </AreaChart>
      </ResponsiveContainer>
    </div>
  );
}

function heatTone(ratio: number, isLight: boolean): { background: string; color: string } {
  if (ratio <= 0.08) return { background: isLight ? '#f1f5f9' : '#1a1a1a', color: isLight ? '#94a3b8' : '#737373' };
  if (ratio < 0.35) return { background: isLight ? '#e0e7ff' : '#1e1b4b', color: isLight ? '#3730a3' : '#c7d2fe' };
  if (ratio < 0.65) return { background: isLight ? '#818cf8' : '#4f46e5', color: '#ffffff' };
  return { background: isLight ? '#4338ca' : '#a5b4fc', color: isLight ? '#ffffff' : '#1e1b4b' };
}

export default function DashboardTab({
  isLight,
}: {
  isLight: boolean;
  onToast?: (type: 'success' | 'error' | 'info', message: string) => void;
}) {
  const { state } = useGovernedMcp();
  const navigate = useNavigate();
  const [range, setRange] = useState<DashboardRange>('7d');
  const [filters, setFilters] = useState(DEFAULT_FILTERS);
  const muted = isLight ? 'text-neutral-500' : 'text-neutral-400';
  const title = isLight ? 'text-neutral-900' : 'text-white';
  const axis = isLight ? '#94a3b8' : '#737373';
  const grid = isLight ? '#f1f5f9' : '#27272a';
  const indigo = isLight ? '#4f46e5' : '#818cf8';
  const sky = isLight ? '#0ea5e9' : '#38bdf8';
  const slate = isLight ? '#94a3b8' : '#a3a3a3';
  const teal = isLight ? '#0f766e' : '#2dd4bf';
  const amber = isLight ? '#d97706' : '#fbbf24';

  const model = useMemo(
    () =>
      buildDashboardModel(range, filters, {
        activity: state.activity,
        published: state.published,
        clients: state.clients,
      }),
    [range, filters, state.activity, state.published, state.clients]
  );

  const heatMax = Math.max(...model.heatmap.cells.map((cell) => cell.value), 1);
  const capMax = Math.max(...model.byCapability.map((item) => item.invocations), 1);
  const govMax = Math.max(...model.governanceTotals.map((item) => item.value), 1);
  const clientMax = Math.max(...model.clients.map((item) => item.requests), 1);
  const outcomeTotal = model.outcomes.reduce((sum, slice) => sum + slice.value, 0) || 1;
  const successShare = Math.round(((model.outcomes.find((item) => item.key === 'successful')?.value || 0) / outcomeTotal) * 100);
  const xInterval = range === '30d' ? 4 : 0;
  const requestSeries =
    filters.endpointEnv === 'all'
      ? [
          { key: 'production', color: indigo, name: 'Production' },
          { key: 'test', color: sky, name: 'Test' },
          { key: 'development', color: slate, name: 'Development' },
        ]
      : [
          {
            key: filters.endpointEnv,
            color: indigo,
            name: ENV_OPTIONS.find((item) => item.id === filters.endpointEnv)?.label || 'Endpoint',
          },
        ];

  const setRangeSafe = (next: DashboardRange) => {
    setRange(next);
    logGovernedMcp('app', 'dashboard_range_change', { range: next });
  };

  const outcomeColor = (key: string) => (isLight ? OUTCOME_COLORS[key]?.light : OUTCOME_COLORS[key]?.dark) || indigo;

  return (
    <div className="space-y-4">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <div>
          <p className={`text-sm font-semibold ${title}`}>Operations</p>
          <p className={`text-xs mt-0.5 ${muted}`}>Live demand, enforcement, and latency on the governed MCP host</p>
        </div>
        <div className="flex flex-wrap items-center gap-2">
          <select
            className={selectClass(isLight)}
            value={filters.endpointEnv}
            onChange={(event) => {
              const endpointEnv = event.target.value as EndpointEnvFilter;
              setFilters((prev) => ({ ...prev, endpointEnv }));
              logGovernedMcp('app', 'dashboard_chart_filter', { chart: 'header', endpointEnv });
            }}
          >
            {ENV_OPTIONS.map((option) => (
              <option key={option.id} value={option.id}>
                {option.label}
              </option>
            ))}
          </select>
          <select
            className={selectClass(isLight)}
            value={filters.sourceKind}
            onChange={(event) => {
              const sourceKind = event.target.value as SourceKindFilter;
              setFilters((prev) => ({ ...prev, sourceKind }));
              logGovernedMcp('app', 'dashboard_chart_filter', { chart: 'header', sourceKind });
            }}
          >
            {SOURCE_OPTIONS.map((option) => (
              <option key={option.id} value={option.id}>
                {option.label}
              </option>
            ))}
          </select>
          <div className={`inline-flex rounded-full p-1 ${isLight ? 'bg-white ring-1 ring-black/[0.04]' : 'bg-neutral-900 ring-1 ring-white/10'}`}>
            {RANGES.map((item) => (
              <button key={item.id} type="button" className={pill(range === item.id, isLight)} onClick={() => setRangeSafe(item.id)}>
                {item.label}
              </button>
            ))}
          </div>
        </div>
      </div>

      <div className="grid grid-cols-2 xl:grid-cols-4 gap-3">
        {[
          { label: 'Requests', value: compactCount(model.kpis.requests), hint: 'Gateway invocations', delta: model.kpis.requestsDelta, spark: model.kpis.sparks.requests, color: indigo },
          { label: 'Success rate', value: `${model.kpis.successRate}%`, hint: 'Allowed executions', delta: model.kpis.successDelta, spark: model.kpis.sparks.success, color: isLight ? '#059669' : '#34d399' },
          { label: 'P95 latency', value: `${model.kpis.p95} ms`, hint: `P50 ${model.kpis.avgP50} ms`, delta: model.kpis.p95Delta, invert: true, spark: model.kpis.sparks.latency, color: amber },
          { label: 'Blocked', value: compactCount(model.kpis.blocked), hint: 'Policy denials', delta: model.kpis.blockedDelta, invert: true, spark: model.kpis.sparks.blocked, color: isLight ? '#e11d48' : '#fb7185' },
        ].map((item) => (
          <div key={item.label} className={`${cardClass(isLight)} flex items-end justify-between gap-3`}>
            <div className="min-w-0">
              <p className={`text-[11px] uppercase tracking-wide ${muted}`}>{item.label}</p>
              <p className={`mt-1 text-2xl font-semibold tabular-nums tracking-tight ${title}`}>{item.value}</p>
              <div className="mt-1 flex items-center gap-2">
                <Delta value={item.delta} invert={item.invert} isLight={isLight} />
                <span className={`text-[11px] ${muted}`}>{item.hint}</span>
              </div>
            </div>
            <Spark values={item.spark} color={item.color} />
          </div>
        ))}
      </div>

      <div className="flex flex-wrap gap-2">
        <span className={`inline-flex items-center gap-1.5 rounded-full px-3 py-1 text-xs ${isLight ? 'bg-amber-50 text-amber-800' : 'bg-amber-500/10 text-amber-300'}`}>
          <Clock3 size={12} />
          {model.kpis.approvalHeld.toLocaleString()} held for approval
        </span>
        <button
          type="button"
          onClick={() => {
            logGovernedMcp('app', 'dashboard_live_tools_open', {});
            navigate(gatewayPathForTab('tools'));
          }}
          className={`inline-flex items-center gap-1.5 rounded-full px-3 py-1 text-xs ${isLight ? 'bg-indigo-50 text-indigo-700 hover:bg-indigo-100' : 'bg-indigo-500/15 text-indigo-300'}`}
        >
          <Wrench size={12} />
          {model.kpis.liveTools} live tools
        </button>
        <span className={`inline-flex items-center gap-1.5 rounded-full px-3 py-1 text-xs ${isLight ? 'bg-neutral-100 text-neutral-600' : 'bg-neutral-800 text-neutral-300'}`}>
          <ShieldAlert size={12} />
          {compactCount(model.governanceTotals.reduce((sum, row) => sum + row.value, 0))} governance events
        </span>
      </div>

      <div className="grid xl:grid-cols-12 gap-4">
        <section className={`${cardClass(isLight)} xl:col-span-8`}>
          <div className="flex items-start justify-between gap-3 mb-3">
            <div>
              <p className={`text-sm font-medium ${title}`}>Request volume</p>
              <p className={`text-xs mt-0.5 ${muted}`}>
                {range === '90d' ? 'Weekly stacked demand by environment' : 'Stacked demand by environment'}
              </p>
            </div>
            <ul className="flex flex-wrap gap-3">
              {requestSeries.map((series) => (
                <li key={series.key} className={`flex items-center gap-1.5 text-xs ${muted}`}>
                  <span className="h-1.5 w-1.5 rounded-full" style={{ background: series.color }} />
                  {series.name}
                </li>
              ))}
            </ul>
          </div>
          <div className="h-72">
            <ResponsiveContainer width="100%" height="100%">
              <AreaChart data={model.requestsOverTime} margin={{ top: 8, right: 8, left: 0, bottom: 0 }}>
                <defs>
                  {requestSeries.map((series) => (
                    <linearGradient key={series.key} id={`ops-req-${series.key}`} x1="0" y1="0" x2="0" y2="1">
                      <stop offset="0%" stopColor={series.color} stopOpacity={isLight ? 0.28 : 0.4} />
                      <stop offset="100%" stopColor={series.color} stopOpacity={0.04} />
                    </linearGradient>
                  ))}
                </defs>
                <CartesianGrid stroke={grid} strokeDasharray="3 3" vertical={false} />
                <XAxis dataKey="label" tick={{ fill: axis, fontSize: 11 }} axisLine={false} tickLine={false} interval={xInterval} minTickGap={16} />
                <YAxis tick={{ fill: axis, fontSize: 11 }} axisLine={false} tickLine={false} width={40} tickFormatter={(value: number) => compactCount(value)} />
                <Tooltip content={<ChartTooltip isLight={isLight} />} />
                {requestSeries.map((series) => (
                  <Area
                    key={series.key}
                    type="monotone"
                    dataKey={series.key}
                    name={series.name}
                    stackId="env"
                    stroke={series.color}
                    fill={`url(#ops-req-${series.key})`}
                    strokeWidth={1.5}
                  />
                ))}
              </AreaChart>
            </ResponsiveContainer>
          </div>
        </section>

        <section className={`${cardClass(isLight)} xl:col-span-4`}>
          <p className={`text-sm font-medium ${title}`}>Enforcement mix</p>
          <p className={`text-xs mt-0.5 mb-4 ${muted}`}>How Selfic decided each request</p>
          <div className="relative h-44">
            <ResponsiveContainer width="100%" height="100%">
              <PieChart>
                <Pie data={model.outcomes} dataKey="value" nameKey="name" innerRadius={56} outerRadius={78} paddingAngle={2} stroke="none">
                  {model.outcomes.map((slice) => (
                    <Cell key={slice.key} fill={outcomeColor(slice.key)} />
                  ))}
                </Pie>
                <Tooltip content={<ChartTooltip isLight={isLight} />} />
              </PieChart>
            </ResponsiveContainer>
            <div className="absolute inset-0 flex flex-col items-center justify-center pointer-events-none">
              <p className={`text-2xl font-semibold tabular-nums ${title}`}>{successShare}%</p>
              <p className={`text-[11px] ${muted}`}>allowed</p>
            </div>
          </div>
          <ul className="mt-3 space-y-2">
            {model.outcomes.map((slice) => (
              <li key={slice.key} className="flex items-center justify-between gap-3 text-sm">
                <span className="flex items-center gap-2 min-w-0">
                  <span className="h-2 w-2 rounded-full shrink-0" style={{ background: outcomeColor(slice.key) }} />
                  <span className={`truncate ${isLight ? 'text-neutral-700' : 'text-neutral-200'}`}>{slice.name}</span>
                </span>
                <span className={`tabular-nums shrink-0 ${muted}`}>
                  {compactCount(slice.value)} · {Math.round((slice.value / outcomeTotal) * 100)}%
                </span>
              </li>
            ))}
          </ul>
        </section>
      </div>

      <div className="grid xl:grid-cols-12 gap-4">
        <section className={`${cardClass(isLight)} xl:col-span-5`}>
          <div className="flex items-center justify-between gap-2 mb-4">
            <div>
              <p className={`text-sm font-medium ${title}`}>Top capabilities</p>
              <p className={`text-xs mt-0.5 ${muted}`}>Invocations and success in this range</p>
            </div>
            <div className={`inline-flex rounded-full p-0.5 ${isLight ? 'bg-neutral-100' : 'bg-neutral-800'}`}>
              {CAP_VIEWS.map((view) => (
                <button
                  key={view.id}
                  type="button"
                  className={pill(filters.capabilityView === view.id, isLight)}
                  onClick={() => {
                    setFilters((prev) => ({ ...prev, capabilityView: view.id }));
                    logGovernedMcp('app', 'dashboard_chart_filter', { chart: 'capability', view: view.id });
                  }}
                >
                  {view.label}
                </button>
              ))}
            </div>
          </div>
          <ol className="space-y-3">
            {model.byCapability.slice(0, 6).map((item, index) => (
              <li key={item.name}>
                <div className="flex items-center justify-between gap-3 mb-1">
                  <span className={`text-sm truncate ${title}`}>
                    <span className={`mr-2 tabular-nums ${muted}`}>{index + 1}</span>
                    {item.name}
                  </span>
                  <span className={`text-xs tabular-nums shrink-0 ${muted}`}>
                    {compactCount(item.invocations)} · {item.successRate}%
                  </span>
                </div>
                <div className={`h-1.5 rounded-full overflow-hidden ${isLight ? 'bg-neutral-100' : 'bg-neutral-800'}`}>
                  <div className="h-full rounded-full bg-indigo-500" style={{ width: `${Math.max(6, Math.round((item.invocations / capMax) * 100))}%` }} />
                </div>
              </li>
            ))}
          </ol>
        </section>

        <section className={`${cardClass(isLight)} xl:col-span-7`}>
          <div className="flex items-start justify-between gap-3 mb-3">
            <div>
              <p className={`text-sm font-medium ${title}`}>Execution latency</p>
              <p className={`text-xs mt-0.5 ${muted}`}>P50 stays tight; P95 shows load and incidents</p>
            </div>
            <ul className="flex gap-3">
              <li className={`flex items-center gap-1.5 text-xs ${muted}`}>
                <span className="h-1.5 w-1.5 rounded-full" style={{ background: teal }} />
                P50
              </li>
              <li className={`flex items-center gap-1.5 text-xs ${muted}`}>
                <span className="h-1.5 w-1.5 rounded-full" style={{ background: amber }} />
                P95
              </li>
            </ul>
          </div>
          <div className="h-64">
            <ResponsiveContainer width="100%" height="100%">
              <AreaChart data={model.latency} margin={{ top: 8, right: 8, left: 0, bottom: 0 }}>
                <defs>
                  <linearGradient id="ops-p50" x1="0" y1="0" x2="0" y2="1">
                    <stop offset="0%" stopColor={teal} stopOpacity={isLight ? 0.2 : 0.3} />
                    <stop offset="100%" stopColor={teal} stopOpacity={0} />
                  </linearGradient>
                  <linearGradient id="ops-p95" x1="0" y1="0" x2="0" y2="1">
                    <stop offset="0%" stopColor={amber} stopOpacity={isLight ? 0.16 : 0.24} />
                    <stop offset="100%" stopColor={amber} stopOpacity={0} />
                  </linearGradient>
                </defs>
                <CartesianGrid stroke={grid} strokeDasharray="3 3" vertical={false} />
                <XAxis dataKey="label" tick={{ fill: axis, fontSize: 11 }} axisLine={false} tickLine={false} interval={xInterval} minTickGap={16} />
                <YAxis tick={{ fill: axis, fontSize: 11 }} axisLine={false} tickLine={false} width={42} tickFormatter={(value: number) => `${value}`} />
                <Tooltip content={<ChartTooltip isLight={isLight} />} />
                <Area type="monotone" dataKey="p95" name="P95 (ms)" stroke={amber} fill="url(#ops-p95)" strokeWidth={2} />
                <Area type="monotone" dataKey="p50" name="P50 (ms)" stroke={teal} fill="url(#ops-p50)" strokeWidth={2} />
              </AreaChart>
            </ResponsiveContainer>
          </div>
        </section>
      </div>

      <div className="grid xl:grid-cols-12 gap-4">
        <section className={`${cardClass(isLight)} xl:col-span-7`}>
          <div className="flex items-start justify-between gap-3 mb-3">
            <div>
              <p className={`text-sm font-medium ${title}`}>Client × capability</p>
              <p className={`text-xs mt-0.5 ${muted}`}>Who is consuming which governed group</p>
            </div>
            <div className="flex items-center gap-1.5">
              <span className={`text-[10px] ${muted}`}>Low</span>
              <span className="h-2 w-8 rounded-full" style={{ background: isLight ? 'linear-gradient(90deg,#e0e7ff,#4338ca)' : 'linear-gradient(90deg,#1e1b4b,#a5b4fc)' }} />
              <span className={`text-[10px] ${muted}`}>High</span>
            </div>
          </div>
          <div className="overflow-x-auto">
            <div
              className="grid gap-1.5 min-w-[480px]"
              style={{ gridTemplateColumns: `6.75rem repeat(${model.heatmap.groups.length}, minmax(0, 1fr))` }}
            >
              <div />
              {model.heatmap.groups.map((group) => (
                <p key={group} className={`text-[10px] text-center truncate ${muted}`} title={group}>
                  {group}
                </p>
              ))}
              {model.heatmap.clients.map((client) => (
                <React.Fragment key={client}>
                  <p className={`text-[11px] truncate pr-2 self-center ${isLight ? 'text-neutral-600' : 'text-neutral-300'}`}>{client}</p>
                  {model.heatmap.groups.map((group) => {
                    const value = model.heatmap.cells.find((cell) => cell.client === client && cell.group === group)?.value ?? 0;
                    const tone = heatTone(value / heatMax, isLight);
                    return (
                      <div
                        key={`${client}-${group}`}
                        title={`${client} · ${group}: ${value.toLocaleString()}`}
                        className="h-11 rounded-lg flex items-center justify-center text-[11px] font-medium tabular-nums"
                        style={tone}
                      >
                        {compactCount(value)}
                      </div>
                    );
                  })}
                </React.Fragment>
              ))}
            </div>
          </div>
        </section>

        <section className={`${cardClass(isLight)} xl:col-span-5 space-y-5`}>
          <div>
            <p className={`text-sm font-medium ${title}`}>Governance caught</p>
            <p className={`text-xs mt-0.5 mb-3 ${muted}`}>Controls that actually fired</p>
            <ul className="space-y-2.5">
              {model.governanceTotals.map((row) => {
                const tone = isLight ? GOVERNANCE_TONES[row.key]?.light : GOVERNANCE_TONES[row.key]?.dark;
                return (
                  <li key={row.key}>
                    <div className="flex items-center justify-between gap-3 mb-1">
                      <span className={`text-sm ${isLight ? 'text-neutral-700' : 'text-neutral-200'}`}>{row.name}</span>
                      <span className={`text-xs tabular-nums ${muted}`}>{compactCount(row.value)}</span>
                    </div>
                    <div className={`h-1.5 rounded-full overflow-hidden ${isLight ? 'bg-neutral-100' : 'bg-neutral-800'}`}>
                      <div className="h-full rounded-full" style={{ width: `${Math.max(5, Math.round((row.value / govMax) * 100))}%`, background: tone }} />
                    </div>
                  </li>
                );
              })}
            </ul>
          </div>
          <div>
            <p className={`text-sm font-medium ${title}`}>Top clients</p>
            <p className={`text-xs mt-0.5 mb-3 ${muted}`}>Share of governed invocations</p>
            <ul className="space-y-2.5">
              {model.clients.map((row) => (
                <li key={row.name}>
                  <div className="flex items-center justify-between gap-3 mb-1">
                    <span className={`text-sm ${title}`}>{row.name}</span>
                    <span className={`text-xs tabular-nums ${muted}`}>
                      {compactCount(row.requests)} · {row.share}%
                    </span>
                  </div>
                  <div className={`h-1.5 rounded-full overflow-hidden ${isLight ? 'bg-neutral-100' : 'bg-neutral-800'}`}>
                    <div className="h-full rounded-full bg-indigo-500" style={{ width: `${Math.max(6, Math.round((row.requests / clientMax) * 100))}%` }} />
                  </div>
                </li>
              ))}
            </ul>
          </div>
        </section>
      </div>
    </div>
  );
}
